import { Fragment, Slice } from '@tiptap/pm/model';
import { BODY_PARAGRAPH_ATTRS } from './post_editor_constants_paragraph.js';

/** Invisible / formatting characters to strip from pasted plain text (keep ZWJ for emoji). */
const CLIPBOARD_INVISIBLE_CHARS =
  /[\u00AD\u034F\u061C\u115F\u1160\u180E\u200B\u200C\u200E\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\u3164\uFEFF\uFFA0\uFFFC]/g;

/** Clipboard plain text that is actually an HTML fragment (devtools, chat). */
const HTML_FRAGMENT_HINT_RE =
  /<(?:\/?(?:p|div|span|a|ul|ol|li|br|h[1-6]|b|i|strong|em|table)\b|!--)/i;

export function clipboardPlainLooksLikeHtml(text) {
  if (typeof text !== 'string') return false;
  const trimmed = text.trim();
  if (!trimmed.length || !trimmed.includes('<')) return false;
  return HTML_FRAGMENT_HINT_RE.test(trimmed);
}

/**
 * Normalize clipboard plain text: unify newlines, nbsp → space, strip invisible chars.
 * Matches ui-refresh branch paste behavior.
 */
export function normalizeClipboardPlainText(text) {
  if (typeof text !== 'string') return '';
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\u2028/g, '\n')
    .replace(/\u2029/g, '\n')
    .replace(/\u00A0/g, ' ')
    .replace(CLIPBOARD_INVISIBLE_CHARS, '');
}

function parseClipboardHtmlDocument(html) {
  const trimmed = html.trim();
  const source =
    trimmed.startsWith('<') && !/^<html[\s>]/i.test(trimmed)
      ? `<!DOCTYPE html><html><head></head><body>${trimmed}</body></html>`
      : trimmed;
  return new DOMParser().parseFromString(source, 'text/html');
}

/** Plain text from clipboard only (ui-refresh). HTML-looking plain → extract text, no tags. */
export function plainTextFromClipboardEvent(event) {
  const plain = event.clipboardData?.getData('text/plain') ?? '';
  if (!plain) return '';

  if (clipboardPlainLooksLikeHtml(plain) && typeof DOMParser !== 'undefined') {
    const doc = parseClipboardHtmlDocument(plain);
    return normalizeClipboardPlainText(doc.body?.innerText ?? doc.body?.textContent ?? '');
  }

  return normalizeClipboardPlainText(plain);
}

function paragraphNode(schema, line) {
  const paragraphType = schema.nodes.paragraph;
  if (!paragraphType) return null;
  const normalized = normalizeClipboardPlainText(line);
  const content = normalized ? [schema.text(normalized)] : [];
  return paragraphType.create(BODY_PARAGRAPH_ATTRS, content);
}

/** Drop trailing blank lines from a paste (avoids empty paragraphs / list debris). */
export function trimTrailingEmptyPasteLines(lines) {
  const result = [...lines];
  while (result.length > 0 && normalizeClipboardPlainText(result[result.length - 1]).trim() === '') {
    result.pop();
  }
  return result;
}

/**
 * Plain-text paste only: one body paragraph per line (blank lines kept except trailing).
 * No lists, bold, links, or other formatting — same as ui-refresh branch.
 */
export function buildPlainTextPasteSlice(schema, text) {
  const normalized = normalizeClipboardPlainText(text);
  const lines = trimTrailingEmptyPasteLines(normalized.split('\n'));
  const nodes = lines.map((line) => paragraphNode(schema, line)).filter(Boolean);
  if (!nodes.length) return null;
  return new Slice(Fragment.from(nodes), 0, 0);
}

/**
 * Paste plain text into a list: one list item per line (matches Enter / splitListItem).
 * Trims trailing blank lines so paste does not leave empty paragraphs at the end.
 */
export function pastePlainTextIntoList(editor, lines) {
  const trimmed = trimTrailingEmptyPasteLines([...lines]);
  if (!trimmed.length) return true;

  const { from, to } = editor.state.selection;
  if (from !== to) {
    editor.chain().focus().deleteRange({ from, to }).run();
  }

  trimmed.forEach((line, index) => {
    const lineText = normalizeClipboardPlainText(line);
    if (index > 0) {
      editor.chain().focus().splitListItem('listItem').run();
    }
    if (lineText) {
      editor.chain().focus().insertContent(lineText).run();
    }
  });

  return true;
}

/**
 * Build paste slice from clipboard — always plain text, newlines preserved.
 */
export function buildPasteSliceFromClipboard(schema, event) {
  const text = plainTextFromClipboardEvent(event);
  return buildPlainTextPasteSlice(schema, text);
}

/** @deprecated Use buildPasteSliceFromClipboard */
export function clipboardPlainTextFromEvent(event) {
  return plainTextFromClipboardEvent(event);
}
